<!-- 
  Receiving Log Component
  -----------------------
  This component handles the display and interaction with the receiving logs. It includes a table to display the logs,
  modals for filtering logs and viewing/editing log details, and a date picker modal for selecting dates.

  Features:
  - Displays logs in a table with pagination and sorting.
  - Provides a filter modal to filter logs based on various criteria.
  - Allows viewing and editing log details in a modal.
  - Includes a date picker modal for selecting dates.

-->
<div class="receiving-log-page-container">
  <!-- Table Container -->
  <div class="table-container">
    <!-- Table Component to display logs -->
    <app-table
      [items]="receivingLogs"
      [totalPages]="totalPageCount"
      [columns]="columnData"
      [dataLoading]="logsLoading"
      [tableConfig]="tableConfig"
      [user]="user"
      [isArchiveTable]="showArchivedLogs"
      (sortByColumn)="onSortByColumn($event)"
      (sortColumnDirection)="onSortByColumnDirection($event)"
      (updatePageNumber)="onUpdatePageNumber($event)"
      (openRowModal)="onOpenRowModal($event)"
      (openFilterModal)="onOpenFilterModal()"
      (navToPage)="onNavToNewLogPage()"
      (deleteRow)="onDeleteLogRow($event)"
      (restoreRow)="onRestoreLogRow($event)"
      (generateEmail)="generateEmailFromLogRow($event)"
      (reprintRow)="onReprintLogRow($event)"
      [isLargeScreen]="isLargeScreen"
    ></app-table>
  </div>

  <!-- The two derived-field editors.

       ⚠️ PAGE LEVEL, not inside the entry-details modal's <ng-template>, even
       though they present ON TOP of it. An `ion-modal` attaches its overlay and
       its backdrop to the document, but the COMPONENT lives where it is declared
       — so declaring these inside the parent's template meant that closing the
       entry modal destroyed them mid-teardown, before Ionic could unwind that
       document-level state.

       ⚠️ THIS DID NOT FIX THE REPORTED BUG. After both editors have been opened
       and closed, the app can still refuse to navigate. The placement is right on
       its own merits — it matches the reprint and email-preview modals below —
       but the cause is still open. See the session handoff before changing this.

       Nesting was always about what the user SEES, not about where the component
       is declared. -->
  <ng-container *ngIf="selectedRow">
    <app-receiving-log-edit
      mode="quantity"
      [isOpen]="openFieldEditor === 'quantity'"
      [logId]="selectedRow.id ?? null"
      (closed)="onRowEditorClosed($event)"
    ></app-receiving-log-edit>

    <app-receiving-log-edit
      mode="items"
      [isOpen]="openFieldEditor === 'items'"
      [logId]="selectedRow.id ?? null"
      (closed)="onRowEditorClosed($event)"
    ></app-receiving-log-edit>
  </ng-container>

  <!-- Reprint Labels Modal -->
  <app-reprint-labels-modal
    [isOpen]="isReprintModalOpen"
    [logRow]="reprintRow"
    (closeModal)="onCloseReprintModal()"
  ></app-reprint-labels-modal>

<!-- Email Preview Modal -->
<app-preview-email-modal
  *ngIf="selectedRow"
  [isOpen]="isEmailPreviewModalOpen"
  [includePhotosInEmail]="includePhotosInEmail"
  [logEntryId]="selectedRow.id"
  [hasExpeditingEmail]="selectedRowHasExpeditingEmail"
  (closeModal)="onCloseEmailPreviewModal()"
  (sendEmail)="onSendEmail()"
  (togglePhotosInEmail)="onTogglePhotosInEmail($event)">
</app-preview-email-modal>

  <!-- Modal for filtering logs -->
  <ion-modal
    class="card-popup"
    [isOpen]="isFilterModalOpen"
    (didDismiss)="closeFilterModal()"
    [backdropDismiss]="true"
  >
    <ng-template>
      <!--Modal header with exit button-->
      <ion-header>
        <ion-toolbar>
          <ion-title>Filter Options</ion-title>
          <ion-buttons slot="end">
            <ion-button (click)="closeFilterModal()"
              ><ion-icon name="close" slot="start"></ion-icon>
              Close</ion-button
            >
          </ion-buttons>
        </ion-toolbar>
      </ion-header>

      <!-- Buttons To clear or apply the selected form filters -->
      <div class="modal-actions-header">
        <ion-button expand="full" type="submit" (click)="onSubmitFilters()">
          Apply Filters
        </ion-button>
        <ion-button expand="full" color="medium" (click)="onClearFilters()">
          Clear All
        </ion-button>
      </div>

      <!-- Filter Options -->
      <ion-content>
        <!-- Checkbox list for additional filter options -->
        <ion-list class="checkbox-list" lines="none">
          <ion-item>
            <!--Checkbox to toggle viewing logs with date out-->
            <ion-checkbox
              labelPlacement="end"
              justify="start"
              [checked]="showLogsWithDateOut"
              (ionChange)="onToggleLogsWithDateOut()"
            >
              Include Entries with "Date Out"
            </ion-checkbox>
          </ion-item>
          <ion-item>
            <!--Checkbox to toggle viewing archived logs-->
            <ion-checkbox
              labelPlacement="end"
              justify="start"
              (ionChange)="onToggleArchivedLogs()"
              [checked]="showArchivedLogs"
            >
              Switch to Archived Table
            </ion-checkbox>
          </ion-item>
        </ion-list>

        <!-- Form for filtering logs based on column data -->
        <form [formGroup]="filterForm">
          <ion-list>
            <ion-list-header>
              <ion-note style="font-size: 18px">
                Filter table by column values:
              </ion-note>
            </ion-list-header>

            <ion-item >
              <!-- Input field for general search -->
              <ion-input
               
                formControlName="_ALL"
               placeholder="Search all fields..."
                type="text"
                [labelPlacement]="filterLogsLabelPlacement"
                label="General Search"
                [clearInput]="true"
              >
              </ion-input>
            </ion-item>

            <!-- Iterate over column data to create filter inputs -->
            <ion-item *ngFor="let column of columnData">
              <!-- Input field for non-date columns -->
              <ion-input
                *ngIf="column.formFieldType !== 'date'"
                [formControlName]="column.name"
                [placeholder]="
                  column.placeholder
                    ? column.placeholder
                    : 'Filter by ' + column.label
                "
                type="text"
                [labelPlacement]="filterLogsLabelPlacement"
                [label]="column.label"
                [clearInput]="true"
              >
              </ion-input>

              <!-- Input field for date columns. 
                   These will open the date modal when clicked into -->
              <ion-input
                *ngIf="column.formFieldType === 'date'"
                [formControlName]="column.name"
                [placeholder]="
                  column.placeholder
                    ? column.placeholder
                    : 'Filter by ' + column.label
                "
                [labelPlacement]="editLogLabelPlacement"
                [label]="column.label"
                [disabled]="column.disabled ? column.disabled : false"
                [clearInput]="true"
                (click)="onOpenDateModal(column.name, filterForm)"
              >
              </ion-input>
            </ion-item>
          </ion-list>
        </form>
      </ion-content>
    </ng-template>
  </ion-modal>

  <!-- Modal to Show Item Details -->
  <ion-modal
    class="card-popup"
    [isOpen]="isRowModalOpen"
    (didDismiss)="closeRowModal()"
    [backdropDismiss]="true"
  >
    <ng-template>
      <!-- Modal Header with toolbar and action buttons -->
      <ion-header>
        <ion-toolbar>
          <!-- Button to open item details in a separate window -->
          <ion-buttons slot="start">
            <ion-button
              fill="clear"
              title="Open Log Details in New Window"
              (click)="openRowDetailsWindow()"
            >
              <ion-icon name="open"></ion-icon>
            </ion-button>
          </ion-buttons>

          <!-- Modal Title — hidden on phones, where the toolbar's icon + action
               buttons already crowd/overlap and the title text isn't needed. -->
          <ion-title *ngIf="!isSmallScreen" slot="start" style="padding-left: 0">
           {{showArchivedLogs ? 'Archived ' : ''}} Entry Details
          </ion-title>

          <!-- Action buttons: Update and Close -->

          <ion-buttons slot="end">
            <!-- No "Edit Rows" button here any more. The two derived strings are
                 edited from a button on the field each one belongs to — see the
                 quantity and items rows below — because they are different edits
                 made with different controls, and a single button could not say
                 which one it was about to open. -->

            <!-- Update Button -->

            <ion-button
              type="submit"
              (click)="updateLogRow()"
              [disabled]="showArchivedLogs"
              class="submit-btn"

              ><ion-icon name="checkmark"></ion-icon>&nbsp;
              Update Entry
            </ion-button>
            <!-- Close Button -->

            <ion-button (click)="closeRowModal()"
              ><ion-icon name="close" slot="start"></ion-icon>
              Close
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>

                <!-- Form to edit log details -->
      <ion-content>

        <!-- What the nested row editor already did.

             PAST TENSE, and deliberately not a pending badge: that editor commits
             in its own transaction, so by the time this shows, the log has moved
             and the fields below have been re-baselined to match. Without this the
             user would close a modal they opened on one version of the entry and
             never be told it is now another. -->
        <div class="advisory advisory-success row-editor-result" *ngIf="rowEditorSummary.length">
          <ion-icon name="checkmark-circle-outline"></ion-icon>
          <div>
            <div class="row-editor-result-title">Rows updated</div>
            <div *ngFor="let line of rowEditorSummary">{{ line }}</div>
          </div>
        </div>

        <!-- Carried out of the row editor because the PUT response is the only
             place it exists — a refetch cannot reconstruct which captions moved.
             Reprinting is a separate, manual step from this table's own reprint
             action; nothing here prints. -->
        <div class="advisory advisory-warning row-editor-label-warning" *ngIf="rowEditorLabelWarning">
          <ion-icon name="warning-outline"></ion-icon>
          <div>
            <div class="row-editor-result-title">Labels need replacing</div>
            <div>{{ rowEditorLabelWarning }}</div>
            <div class="row-editor-caption" *ngFor="let caption of rowEditorRenumberedCaptions">
              {{ caption }}
            </div>
          </div>
        </div>

        <form [formGroup]="editLogForm">
          <ion-list>
                          <!-- Loop through column data to dynamically generate form fields -->
            <ng-container *ngFor="let column of columnData">
            <ion-item class="detail-item"
                      [class.detail-item-locked]="isFieldLockedByRows(column)">
                              <!-- Text Input Field -->

              <ion-input
                *ngIf="column.formFieldType === 'input'"
                [formControlName]="column.name"
                [placeholder]="
                 ( column.disabled && !userIsAdmin)
                    ? ''
                    : column.placeholder
                    ? column.placeholder
                    : 'Enter ' + column.label.toLowerCase()
                "
                [type]="column.inputType ? column.inputType : 'text'"
                [labelPlacement]="editLogLabelPlacement"
                [label]="column.label"
           [clearInput]="shouldShowClearField(column)"
                (ionFocus)="onFieldFocus(column)"
                (ionBlur)="onFieldBlur(column)"
              >
              </ion-input>
                <!-- Select Dropdown Field -->
              <ion-select
                *ngIf="column.formFieldType === 'select'"
                [aria-label]="column.label"
                interface="popover"
                [formControlName]="column.name"
                [placeholder]="
                  ( column.disabled && !userIsAdmin)
                    ? ''
                    : column.placeholder
                    ? column.placeholder
                    : 'Enter ' + column.label.toLowerCase()
                "
                [labelPlacement]="editLogLabelPlacement"
                [label]="column.label"
                [clearInput]="shouldShowClearField(column)"
              >
                <ion-select-option
                  *ngFor="let option of column.options"
                  [value]="option.value"
                >
                  {{ option.label }}
                </ion-select-option>
              </ion-select>

                <!-- Textarea Field for multiline input -->
              <ion-textarea
                *ngIf="column.formFieldType === 'textarea'"
                [type]="column.inputType ? column.inputType : 'text'"
                [formControlName]="column.name"
                [placeholder]="
                  ( column.disabled && !userIsAdmin)
                    ? ''
                    : column.placeholder
                    ? column.placeholder
                    : 'Enter ' + column.label.toLowerCase()
                "
                [labelPlacement]="editLogLabelPlacement"
                [label]="column.label"
           [clearInput]="shouldShowClearField(column)"
              >
              </ion-textarea>

                <!-- Date Input Field (Triggers Date Picker Modal) -->
              <ion-input
                *ngIf="column.formFieldType === 'date'"
                [formControlName]="column.name"
                [placeholder]="
                  ( column.disabled && !userIsAdmin)
                    ? ''
                    : column.placeholder
                    ? column.placeholder
                    : 'Enter ' + column.label.toLowerCase()
                "
                [labelPlacement]="editLogLabelPlacement"
                [label]="column.label"
            [clearInput]="shouldShowClearField(column)"
                (click)="onOpenDateModal(column.name, editLogForm)"
              >
              </ion-input>

              <!-- A derived field's own editor, on the field itself.

                   Both `quantity` and `items` are projections of child rows once a
                   log has them — one composed by the backend from the containers,
                   one by us from the receipts — so neither is typed over here. The
                   button is on the field rather than in the toolbar because the
                   two are different edits made with different controls.

                   The EXPLANATION is a hover tooltip, not standing text: it is the
                   same sentence on every log and the button already says what to
                   do, so leaving it on screen only competes with the field for
                   attention. -->
              <div class="locked-field-block" *ngIf="lockedFieldHint(column) as hint">
                <ion-icon
                  class="locked-field-info"
                  name="information-circle-outline"
                  [title]="hint"
                  [attr.aria-label]="hint"
                ></ion-icon>
                <ion-button
                  *ngIf="!showArchivedLogs && editorFor(column) as editor"
                  size="small"
                  fill="outline"
                  class="locked-field-btn"
                  (click)="openFieldEditorFor(editor)"
                  [title]="hint"
                >
                  <ion-icon name="create-outline" slot="start"></ion-icon>
                  {{ editor === 'items' ? 'Edit items' : 'Edit containers' }}
                </ion-button>
              </div>
            </ion-item>

            <!-- Type-ahead for the two directory-backed fields (Email, Logged By).
                 A zero-height relative anchor, so the dropdown's `top: 100%` lands
                 directly under the item it belongs to without the item itself
                 having to become a positioned, overflow-visible container.

                 Deliberately NOT an ion-select: a select renders an element per
                 option as soon as it exists, and the employee directory rendered
                 twice over locks the modal up on open. Here the list only ever
                 materializes the handful of rows that match what's been typed. -->
            <div class="detail-autocomplete-anchor"
                 *ngIf="employeeChoicesFor(column) as employeeChoices">
              <app-autocomplete-dropdown
                [options]="employeeChoices"
                [formGroup]="editLogForm"
                [controlName]="column.name"
                [active]="activeFieldName === column.name"
              >
              </app-autocomplete-dropdown>
            </div>
            </ng-container>
          </ion-list>
        </form>


        <div class="log-photo-container"
        *ngIf="!isPhotoManagerModalOpen">
        <ion-text color="medium"> 
          <h2>Log Entry Photos</h2>
        </ion-text>
                <ion-item
                *ngIf="photosLoading.isLoading"
                >
            <div class="spinner-container">
              <ion-spinner name="dots"></ion-spinner> {{ photosLoading.message }}
            </div>
          </ion-item>
        <!-- One labelled grid per kind ("Packing slips", …) rather than a label on
             every thumbnail. Read-only here, so splitting the list costs nothing:
             no delete indexes point back into it. -->
        <ng-container *ngIf="!photosLoading.isLoading">
          <div class="photo-kind-group" *ngFor="let group of logRowPhotoGroups">
            <h3 class="photo-kind-heading">
              {{ group.label }}
              <span class="photo-kind-count">{{ group.photos.length }}</span>
            </h3>
            <app-photo-grid-viewer
            [photos]="group.photos"
            [viewingLogRow]="true"
            [photosEditable]="false"
            ></app-photo-grid-viewer>
          </div>

          <!-- The grid's own empty state, kept for a log with no photos at all -->
          <app-photo-grid-viewer
          *ngIf="logRowPhotoGroups.length === 0"
          [photos]="[]"
          [viewingLogRow]="true"
          [photosEditable]="false"
          ></app-photo-grid-viewer>
        </ng-container>
        <ion-button (click)="onOpenPhotoManager()" *ngIf="user.name===selectedRow.loggedBy || user.isAdmin" >
          <ion-icon name="camera" slot="start"></ion-icon>
          Update Photos
        </ion-button>
        <ion-button fill="outline" (click)="downloadAllPhotos()" *ngIf="totalLogRowPhotoCount">
          <ion-icon name="download-outline" slot="start"></ion-icon>
          Download all
        </ion-button>
        </div>

        <app-log-photo-manager
        *ngIf="selectedRow.id && isPhotoManagerModalOpen"
        [logId]="selectedRow.id"
        [poNumber]="selectedRow.poNumber || ''"
        (closeModal)="onClosePhotoManager()"

        ></app-log-photo-manager>



      </ion-content>

    </ng-template>
  </ion-modal>



  <!-- Modal to Show datepicker -->
  <ion-modal
    class="date-popup"
    [isOpen]="isDateModalOpen"
    (didDismiss)="closeDateModal()"
  >
    <ng-template>
              <!-- Custom Date Picker Component -->
      <app-date-picker
        [startDate]="tempSelectedDate"
        (dateSelected)="updateSelectedDate($event)"
        (cancelDatePicker)="cancelDateSelection()"
        (confirmSelectedDate)="closeDateModal()"
      ></app-date-picker>
    </ng-template>
  </ion-modal>

</div>
